import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useProfile } from '../context/ProfileContext';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';

const { FiEdit2, FiPlus, FiClock, FiCheck, FiAlertCircle, FiTrendingUp, FiShare2, FiLink, FiGlobe, FiX, FiCopy } = FiIcons;

const BaseResume = () => {
  const navigate = useNavigate();
  const { data, refreshData } = useProfile();
  const baseUserResume = data.user;
  const { session } = useAuth();

  const sortedExperience = React.useMemo(() => {
    if (!baseUserResume?.experience) return [];
    return [...baseUserResume.experience].sort((a, b) => {
      // Basic sorting logic based on duration string parsing
      const isAPresent = a.duration?.toLowerCase().includes('present');
      const isBPresent = b.duration?.toLowerCase().includes('present');
      if (isAPresent && !isBPresent) return -1;
      if (!isAPresent && isBPresent) return 1;

      // Try to extract years if both aren't "Present" or both are
      const yearA = a.duration?.match(/\d{4}/g)?.pop() || 0;
      const yearB = b.duration?.match(/\d{4}/g)?.pop() || 0;
      return parseInt(yearB) - parseInt(yearA);
    });
  }, [baseUserResume?.experience]);

  const [showVersions, setShowVersions] = useState(false);
  const [sharingVersion, setSharingVersion] = useState(null);
  const [copied, setCopied] = useState(false);
  const [resumeVersions, setResumeVersions] = useState([]);

  const [showAddSkill, setShowAddSkill] = useState(false);
  const [newSkill, setNewSkill] = useState('');

  const [showAddRole, setShowAddRole] = useState(false);
  const [showEditRole, setShowEditRole] = useState(false);
  const [editingRole, setEditingRole] = useState({
    id: null,
    role: '',
    company: '',
    duration: '',
    bullets: ''
  });

  const [newRole, setNewRole] = useState({
    role: '',
    company: '',
    duration: '',
    bullets: ''
  });

  useEffect(() => {
    const fetchResumes = async () => {
      // Empty versions initially. Hook to real table when ready.
      setResumeVersions([]);
    };
    if (session) fetchResumes();
  }, [session]);

  const handleCopy = (url) => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="p-8 max-w-7xl mx-auto flex flex-col lg:flex-row gap-8">
      <div className="flex-1 pb-24">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Base Resume Foundation</h1>
            <p className="text-gray-500 mt-1">This is your master copy. The AI uses this to generate tailored versions.</p>
          </div>
          <div className="flex gap-3">
            <button 
              onClick={() => setShowVersions(!showVersions)}
              className={`px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-all ${
                showVersions ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <SafeIcon icon={FiClock} /> {showVersions ? 'Hide Versions' : 'History'}
            </button>
            <button
              onClick={() => navigate('/settings')}
              className="bg-white border border-gray-200 px-4 py-2 rounded-xl text-sm font-bold hover:bg-gray-50 flex items-center gap-2"
            >
              <SafeIcon icon={FiEdit2} /> Edit Master
            </button>
          </div>
        </div>

        <div className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {baseUserResume.aiFeedback.map((feedback) => (
            <div 
              key={feedback.id} 
              className={`p-4 rounded-2xl border ${
                feedback.type === 'strength' 
                  ? 'bg-green-50 border-green-100 text-green-700' 
                  : feedback.type === 'improvement' 
                    ? 'bg-blue-50 border-blue-100 text-blue-700' 
                    : 'bg-orange-50 border-orange-100 text-orange-700'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <SafeIcon icon={feedback.type === 'strength' ? FiCheck : feedback.type === 'improvement' ? FiTrendingUp : FiAlertCircle} />
                <span className="text-[10px] font-black uppercase tracking-wider">{feedback.type}</span>
              </div>
              <p className="text-sm font-medium leading-relaxed">{feedback.text}</p>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-10 border-b border-gray-100 bg-gray-50/30">
            <h2 className="text-3xl font-black text-gray-900">{baseUserResume.name}</h2>
            <p className="text-xl text-blue-600 font-bold mt-1">{baseUserResume.title}</p>
            <p className="mt-6 text-gray-600 leading-relaxed text-lg max-w-4xl">{baseUserResume.summary}</p>
          </div>

          <div className="p-10 border-b border-gray-100">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900 uppercase tracking-tight">Core Skills Vault</h3>
              <button onClick={() => setShowAddSkill(true)} className="text-blue-600 text-sm font-black flex items-center gap-1 hover:underline">
                <SafeIcon icon={FiPlus} /> Add Skill
              </button>
            </div>
            <div className="flex flex-wrap gap-3">
              {baseUserResume.skills && baseUserResume.skills.map(skill => (
                <span key={skill} className="px-4 py-2 bg-gray-50 text-gray-700 rounded-xl text-sm font-bold border border-gray-100 hover:border-blue-200 transition-colors">
                  {skill}
                </span>
              ))}
            </div>
          </div>

          <div className="p-10">
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-xl font-bold text-gray-900 uppercase tracking-tight">Experience Blocks</h3>
              <button onClick={() => setShowAddRole(true)} className="text-blue-600 text-sm font-black flex items-center gap-1 hover:underline">
                <SafeIcon icon={FiPlus} /> Add Role
              </button>
            </div>
            <div className="space-y-12">
              {sortedExperience.map((exp, idx) => (
                <div key={idx} className="relative pl-8 border-l-2 border-gray-100 pb-2 last:pb-0">
                  <div className="absolute w-4 h-4 bg-blue-500 rounded-full -left-[9px] top-1.5 border-4 border-white shadow-sm shadow-blue-500/50"></div>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="font-black text-gray-900 text-xl uppercase tracking-tight">{exp.role}</h4>
                      <p className="text-blue-600 font-bold text-lg">{exp.company}</p>
                    </div>
                    <div className="flex gap-3 items-center">
                      <span className="text-xs font-black text-gray-400 bg-gray-50 px-4 py-1.5 rounded-full border border-gray-100">{exp.duration}</span>
                      <button onClick={() => {
                        setEditingRole({
                          id: exp.id,
                          role: exp.role,
                          company: exp.company,
                          duration: exp.duration,
                          bullets: (exp.bullets || []).join('\n')
                        });
                        setShowEditRole(true);
                      }} className="text-blue-600 hover:text-blue-800 transition-colors p-1"><SafeIcon icon={FiEdit2} /></button>
                      <button onClick={async () => {
                        await supabase.from('experiences').delete().eq('id', exp.id);
                        refreshData();
                      }} className="text-red-500 hover:text-red-700 transition-colors p-1"><SafeIcon icon={FiX} /></button>
                    </div>
                  </div>
                  <ul className="list-disc list-outside ml-4 mt-4 space-y-3 text-gray-600 text-lg">
                    {exp.bullets.map((bullet, i) => (
                      <li key={i} className="pl-2 leading-relaxed">{bullet}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {baseUserResume.education && baseUserResume.education.length > 0 && (
            <div className="p-10 border-t border-gray-100">
              <div className="flex justify-between items-center mb-8">
                <h3 className="text-xl font-bold text-gray-900 uppercase tracking-tight">Education</h3>
              </div>
              <div className="space-y-8">
                {baseUserResume.education.map((edu, idx) => (
                  <div key={idx} className="relative pl-8 border-l-2 border-gray-100 pb-2 last:pb-0">
                    <div className="absolute w-4 h-4 bg-blue-500 rounded-full -left-[9px] top-1.5 border-4 border-white shadow-sm shadow-blue-500/50"></div>
                    <div className="flex justify-between items-start mb-1">
                      <div>
                        <h4 className="font-black text-gray-900 text-xl uppercase tracking-tight">{edu.degree}</h4>
                        <p className="text-blue-600 font-bold text-lg">{edu.school}</p>
                      </div>
                      <span className="text-xs font-black text-gray-400 bg-gray-50 px-4 py-1.5 rounded-full border border-gray-100">{edu.duration}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(baseUserResume.gpa ||
           (baseUserResume.publications && baseUserResume.publications.length > 0) ||
           (baseUserResume.affiliations && baseUserResume.affiliations.length > 0) ||
           (baseUserResume.certifications && baseUserResume.certifications.length > 0) ||
           (baseUserResume.organizations && baseUserResume.organizations.length > 0)) && (
            <div className="p-10 border-t border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 uppercase tracking-tight mb-6">Additional Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {baseUserResume.gpa && (
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2 uppercase text-sm tracking-widest text-gray-400">GPA</h4>
                    <p className="text-gray-700 font-medium">{baseUserResume.gpa}</p>
                  </div>
                )}

                {baseUserResume.certifications && baseUserResume.certifications.length > 0 && (
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 uppercase text-sm tracking-widest text-gray-400">Certifications</h4>
                    <ul className="list-disc list-inside text-gray-700 space-y-1">
                      {baseUserResume.certifications.map((cert, idx) => (
                        <li key={idx}>{cert}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {baseUserResume.publications && baseUserResume.publications.length > 0 && (
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 uppercase text-sm tracking-widest text-gray-400">Publications</h4>
                    <ul className="list-disc list-inside text-gray-700 space-y-1">
                      {baseUserResume.publications.map((pub, idx) => (
                        <li key={idx}>{pub}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {baseUserResume.affiliations && baseUserResume.affiliations.length > 0 && (
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 uppercase text-sm tracking-widest text-gray-400">Affiliations</h4>
                    <ul className="list-disc list-inside text-gray-700 space-y-1">
                      {baseUserResume.affiliations.map((affil, idx) => (
                        <li key={idx}>{affil}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {baseUserResume.organizations && baseUserResume.organizations.length > 0 && (
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 uppercase text-sm tracking-widest text-gray-400">Organizations</h4>
                    <ul className="list-disc list-inside text-gray-700 space-y-1">
                      {baseUserResume.organizations.map((org, idx) => (
                        <li key={idx}>{org}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {showVersions && (
          <motion.div 
            initial={{ opacity: 0, x: 50, width: 0 }}
            animate={{ opacity: 1, x: 0, width: 350 }}
            exit={{ opacity: 0, x: 50, width: 0 }}
            className="bg-white border-l border-gray-100 h-[calc(100vh-120px)] sticky top-24 overflow-hidden rounded-3xl shadow-2xl"
          >
            <div className="p-6 border-b border-gray-50 flex items-center justify-between bg-gray-50/50">
              <h3 className="font-black text-gray-900 flex items-center gap-2 uppercase tracking-tight">
                <SafeIcon icon={FiClock} className="text-blue-600" /> Version History
              </h3>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto h-full custom-scrollbar pb-24">
              {resumeVersions.map((v) => (
                <div key={v.id} className="p-5 rounded-3xl border border-gray-100 hover:border-blue-200 hover:bg-blue-50/30 transition-all group">
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="text-sm font-black text-gray-900 group-hover:text-blue-700 leading-tight uppercase tracking-tight">{v.name}</h4>
                    <span className="text-[10px] font-black text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md">{v.score}%</span>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-gray-400 mb-4 font-black uppercase tracking-wider">
                    <SafeIcon icon={FiClock} /> {v.date}
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap gap-1.5">
                      {v.tags.map(tag => (
                        <span key={tag} className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-[9px] font-black uppercase tracking-tighter">#{tag}</span>
                      ))}
                    </div>
                    <button 
                      onClick={() => setSharingVersion(v)}
                      className={`p-2 rounded-lg transition-all ${
                        v.isPublic ? 'text-green-600 bg-green-50 hover:bg-green-100' : 'text-gray-400 hover:bg-gray-100'
                      }`}
                    >
                      <SafeIcon icon={FiShare2} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {sharingVersion && (
          <>
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setSharingVersion(null)}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: "-50%", x: "-50%" }}
              animate={{ opacity: 1, scale: 1, y: "-50%", x: "-50%" }}
              exit={{ opacity: 0, scale: 0.9, y: "-50%", x: "-50%" }}
              className="fixed left-1/2 top-1/2 w-[450px] bg-white rounded-3xl shadow-2xl z-[70] p-8 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-black text-gray-900 uppercase">Share Resume Version</h3>
                <button onClick={() => setSharingVersion(null)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                  <SafeIcon icon={FiX} className="text-gray-400" />
                </button>
              </div>

              <div className="space-y-6">
                <div className="p-4 bg-blue-50 border border-blue-100 rounded-2xl flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center text-white text-xl">
                    <SafeIcon icon={FiGlobe} />
                  </div>
                  <div>
                    <p className="text-sm font-black text-blue-900 uppercase">Public Access</p>
                    <p className="text-xs text-blue-600">Anyone with the link can view this version.</p>
                  </div>
                  <div className="ml-auto">
                    <div className="relative inline-block w-10 h-6">
                      <input type="checkbox" className="hidden" checked={sharingVersion.isPublic} readOnly />
                      <div className={`w-10 h-6 rounded-full transition-colors ${sharingVersion.isPublic ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
                      <div className={`absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform ${sharingVersion.isPublic ? 'translate-x-4' : ''}`}></div>
                    </div>
                  </div>
                </div>

                {sharingVersion.isPublic && (
                  <div className="space-y-3">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Sharable URL</label>
                    <div className="flex gap-2">
                      <div className="flex-1 bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 text-sm text-gray-600 font-medium truncate">
                        {sharingVersion.shareUrl || 'https://ats-pass.app/share/v1/preview'}
                      </div>
                      <button 
                        onClick={() => handleCopy(sharingVersion.shareUrl || 'https://ats-pass.app/share/v1/preview')}
                        className={`px-4 py-3 rounded-xl font-black text-xs transition-all flex items-center gap-2 ${
                          copied ? 'bg-green-500 text-white' : 'bg-gray-900 text-white hover:bg-blue-600'
                        }`}
                      >
                        <SafeIcon icon={copied ? FiCheck : FiCopy} /> {copied ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-8 pt-8 border-t border-gray-50 flex gap-3">
                <button className="flex-1 py-4 bg-gray-100 text-gray-500 rounded-2xl font-black text-xs uppercase hover:bg-gray-200 transition-all">
                  Manage Permissions
                </button>
                <button className="flex-1 py-4 bg-blue-600 text-white rounded-2xl font-black text-xs uppercase hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/25">
                  Save Changes
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAddSkill && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowAddSkill(false)}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: "-50%", x: "-50%" }}
              animate={{ opacity: 1, scale: 1, y: "-50%", x: "-50%" }}
              exit={{ opacity: 0, scale: 0.9, y: "-50%", x: "-50%" }}
              className="fixed left-1/2 top-1/2 w-[450px] bg-white rounded-3xl shadow-2xl z-[70] p-8 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-black text-gray-900 uppercase">Add New Skill</h3>
                <button onClick={() => setShowAddSkill(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                  <SafeIcon icon={FiX} className="text-gray-400" />
                </button>
              </div>
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="e.g. React.js, Python, Marketing"
                  value={newSkill}
                  onChange={(e) => setNewSkill(e.target.value)}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 outline-none focus:border-blue-500"
                />
              </div>
              <div className="mt-8 pt-8 border-t border-gray-50 flex gap-3">
                <button onClick={() => setShowAddSkill(false)} className="flex-1 py-4 bg-gray-100 text-gray-500 rounded-2xl font-black text-xs uppercase hover:bg-gray-200 transition-all">
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!newSkill) return;
                    const updatedSkills = [...(baseUserResume.skills || []), newSkill];
                    const { error } = await supabase
                      .from('profiles')
                      .update({ skills: updatedSkills })
                      .eq('id', session.user.id);
                    if (!error) {
                      setNewSkill('');
                      setShowAddSkill(false);
                      refreshData();
                    }
                  }}
                  className="flex-1 py-4 bg-blue-600 text-white rounded-2xl font-black text-xs uppercase hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/25"
                >
                  Save Skill
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAddRole && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowAddRole(false)}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: "-50%", x: "-50%" }}
              animate={{ opacity: 1, scale: 1, y: "-50%", x: "-50%" }}
              exit={{ opacity: 0, scale: 0.9, y: "-50%", x: "-50%" }}
              className="fixed left-1/2 top-1/2 w-[600px] bg-white rounded-3xl shadow-2xl z-[70] p-8 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-black text-gray-900 uppercase">Add Experience Role</h3>
                <button onClick={() => setShowAddRole(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                  <SafeIcon icon={FiX} className="text-gray-400" />
                </button>
              </div>
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Role (e.g. Senior Frontend Engineer)"
                  value={newRole.role}
                  onChange={(e) => setNewRole({...newRole, role: e.target.value})}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 outline-none focus:border-blue-500"
                />
                <input
                  type="text"
                  placeholder="Company"
                  value={newRole.company}
                  onChange={(e) => setNewRole({...newRole, company: e.target.value})}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 outline-none focus:border-blue-500"
                />
                <input
                  type="text"
                  placeholder="Duration (e.g. Jan 2020 - Present)"
                  value={newRole.duration}
                  onChange={(e) => setNewRole({...newRole, duration: e.target.value})}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 outline-none focus:border-blue-500"
                />
                <textarea
                  placeholder="Bullet points (one per line)"
                  value={newRole.bullets}
                  onChange={(e) => setNewRole({...newRole, bullets: e.target.value})}
                  rows={5}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 outline-none focus:border-blue-500 resize-none"
                />
              </div>
              <div className="mt-8 pt-8 border-t border-gray-50 flex gap-3">
                <button onClick={() => setShowAddRole(false)} className="flex-1 py-4 bg-gray-100 text-gray-500 rounded-2xl font-black text-xs uppercase hover:bg-gray-200 transition-all">
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!newRole.role || !newRole.company || !newRole.duration) return;
                    const bulletsArray = newRole.bullets.split('\n').filter(b => b.trim() !== '');
                    const { error } = await supabase
                      .from('experiences')
                      .insert({
                        profile_id: session.user.id,
                        role: newRole.role,
                        company: newRole.company,
                        duration: newRole.duration,
                        bullets: bulletsArray
                      });
                    if (!error) {
                      setNewRole({ role: '', company: '', duration: '', bullets: '' });
                      setShowAddRole(false);
                      refreshData();
                    }
                  }}
                  className="flex-1 py-4 bg-blue-600 text-white rounded-2xl font-black text-xs uppercase hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/25"
                >
                  Save Role
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showEditRole && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowEditRole(false)}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: "-50%", x: "-50%" }}
              animate={{ opacity: 1, scale: 1, y: "-50%", x: "-50%" }}
              exit={{ opacity: 0, scale: 0.9, y: "-50%", x: "-50%" }}
              className="fixed left-1/2 top-1/2 w-[600px] bg-white rounded-3xl shadow-2xl z-[70] p-8 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-black text-gray-900 uppercase">Edit Experience Role</h3>
                <button onClick={() => setShowEditRole(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                  <SafeIcon icon={FiX} className="text-gray-400" />
                </button>
              </div>
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Role (e.g. Senior Frontend Engineer)"
                  value={editingRole.role}
                  onChange={(e) => setEditingRole({...editingRole, role: e.target.value})}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 outline-none focus:border-blue-500"
                />
                <input
                  type="text"
                  placeholder="Company"
                  value={editingRole.company}
                  onChange={(e) => setEditingRole({...editingRole, company: e.target.value})}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 outline-none focus:border-blue-500"
                />
                <input
                  type="text"
                  placeholder="Duration (e.g. Jan 2020 - Present)"
                  value={editingRole.duration}
                  onChange={(e) => setEditingRole({...editingRole, duration: e.target.value})}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 outline-none focus:border-blue-500"
                />
                <textarea
                  placeholder="Bullet points (one per line)"
                  value={editingRole.bullets}
                  onChange={(e) => setEditingRole({...editingRole, bullets: e.target.value})}
                  rows={5}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 outline-none focus:border-blue-500 resize-none"
                />
              </div>
              <div className="mt-8 pt-8 border-t border-gray-50 flex gap-3">
                <button onClick={() => setShowEditRole(false)} className="flex-1 py-4 bg-gray-100 text-gray-500 rounded-2xl font-black text-xs uppercase hover:bg-gray-200 transition-all">
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!editingRole.role || !editingRole.company || !editingRole.duration) return;
                    const bulletsArray = editingRole.bullets.split('\n').filter(b => b.trim() !== '');
                    const { error } = await supabase
                      .from('experiences')
                      .update({
                        role: editingRole.role,
                        company: editingRole.company,
                        duration: editingRole.duration,
                        bullets: bulletsArray
                      })
                      .eq('id', editingRole.id);
                    if (!error) {
                      setShowEditRole(false);
                      refreshData();
                    }
                  }}
                  className="flex-1 py-4 bg-blue-600 text-white rounded-2xl font-black text-xs uppercase hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/25"
                >
                  Save Changes
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};
export default BaseResume;